"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MuTxtFooter = void 0;
const tslib_1 = require("tslib");
const React = tslib_1.__importStar(require("react"));
const nano_theme_1 = require("nano-theme");
const context_1 = require("@jsonjoy.com/ui/lib/styles/context");
const Label_1 = require("@jsonjoy.com/ui/lib/1-inline/Label");
const Favicon_1 = require("@jsonjoy.com/ui/lib/1-inline/Favicon");
const CopyCode_1 = require("@jsonjoy.com/ui/lib/1-inline/CopyCode");
const CopyButton_1 = require("@jsonjoy.com/ui/lib/2-inline-block/CopyButton");
const Breadcrumbs_1 = require("@jsonjoy.com/ui/lib/3-list-item/Breadcrumbs");
const context_2 = require("../../context");
const util_1 = require("../../util");
const typeToLabel_1 = require("../../util/typeToLabel");
const TranslitFooterPill_1 = require("../../translit/TranslitFooterPill");
const footerClass = (0, nano_theme_1.rule)({
    d: 'flex',
    jc: 'space-between',
    ai: 'center',
    fw: 'wrap',
    gap: '12px',
    pd: '0 18px',
    h: '48px',
    fz: '12px',
});
const footerGroupClass = (0, nano_theme_1.rule)({
    d: 'inline-flex',
    ai: 'center',
    gap: '12px',
    fw: 'wrap',
});
const statusPathClass = (0, nano_theme_1.rule)({
    d: 'inline-flex',
    ai: 'center',
    gap: '12px',
    fw: 'wrap',
});
const pathLinkClass = (0, nano_theme_1.rule)({
    d: 'inline-block',
    maxW: '220px',
    ws: 'nowrap',
    ov: 'hidden',
    textOverflow: 'ellipsis',
    verticalAlign: 'bottom',
    textDecoration: 'underline',
    textDecorationThickness: '1px',
    textUnderlineOffset: '4px',
    textDecorationColor: 'rgb(from currentColor r g b / 0.2)',
    '&:hover': {
        textDecorationColor: 'currentColor',
    },
});
const MuTxtFooter = () => {
    const styles = (0, context_1.useStyles)();
    const state = (0, context_2.useMuTxt)();
    const footerRef = React.useRef(null);
    const availableWidth = state.sizer.width.use();
    const desiredWidth = state.sizer.content.use();
    const focused = state.focused.use();
    const linkOpen = state.inline.link.open.use();
    const readOnly = state.readOnly.use();
    const wordCount = state.wordCount.use();
    const characterCount = state.characterCount.use();
    const selectionText = state.selectionText.use();
    const caretPath = state.caretPath.use();
    const caretLinkHref = state.caretLinkHref.use();
    const caretEmbedUrl = state.caretEmbedUrl.use();
    const caretCodeText = state.caretCodeText.use();
    const caretMathThingId = state.caretMathThingId.use();
    state.things.version.use();
    const caretMathTex = caretMathThingId
        ? (state.things.get(caretMathThingId)?.val ?? '')
        : '';
    const width = Math.min(availableWidth, desiredWidth);
    const infoColor = styles.g(0.34);
    const selectionSummary = selectionText
        ? `${(0, util_1.pluralize)((0, util_1.getWordCount)(selectionText), 'word')} (${(0, util_1.pluralize)(selectionText.length, 'char')}) selected`
        : '';
    const statusText = readOnly ? 'Read-only' : focused ? 'Editing' : '';
    const footerUrl = caretEmbedUrl || caretLinkHref;
    const showCaretPath = width > 700;
    const showCaretCode = width > 900;
    const showCaretUrl = width > 1100;
    const showCharacterCount = width > 500;
    const showSelectionSummary = width > 600;
    const preventBreadcrumbMouseDown = (event) => {
        event.preventDefault();
    };
    const handleLinkBreadcrumbClick = (event) => {
        event.preventDefault();
        const shell = state.shellEl;
        const footer = footerRef.current;
        if (!shell || !footer)
            return;
        const shellRect = shell.getBoundingClientRect();
        const footerRect = footer.getBoundingClientRect();
        const popupWidth = Math.max(Math.min(560, window.innerWidth * 0.4), 320);
        // Offset by LinkFloater's internal GAP (8px) so the popup sits flush against the footer.
        const rect = new DOMRect(shellRect.left, footerRect.top + 8, popupWidth, 0);
        state.inline.link.setAnchorRect(rect);
        state.inline.dismissed.next(true);
        state.inline.link.toggle();
    };
    return (React.createElement("div", { ref: footerRef, className: footerClass, style: { color: infoColor, padding: width < 700 ? '0 16px' : void 0, borderTop: `1px solid ${styles.g(0, 0.06)}` } },
        React.createElement("div", { className: footerGroupClass },
            !!statusText && React.createElement(Label_1.Label, null, statusText),
            !readOnly && (focused || linkOpen) && !!caretPath && showCaretPath && (React.createElement("span", { className: statusPathClass },
                React.createElement(Breadcrumbs_1.Breadcrumbs, { compact: true, crumbs: caretPath.map((segment, index) => (React.createElement(Breadcrumbs_1.Breadcrumb, { key: `${index}:${segment}`, compact: true, onMouseDown: preventBreadcrumbMouseDown, onClick: segment === 'link' ? handleLinkBreadcrumbClick : undefined }, (0, typeToLabel_1.typeToLabel)(segment) || segment))) }),
                !!footerUrl && showCaretUrl && (React.createElement(React.Fragment, null,
                    React.createElement("span", { style: { opacity: 0.25 } }, '→'),
                    React.createElement(Favicon_1.Favicon, { url: footerUrl, size: 16 }),
                    React.createElement("a", { className: pathLinkClass, href: footerUrl, target: "_blank", rel: "noreferrer noopener", title: footerUrl, onMouseDown: (event) => {
                            event.preventDefault();
                        } }, footerUrl),
                    React.createElement(CopyButton_1.CopyButton, { type: "button", width: 24, height: 24, rounder: true, onMouseDown: (event) => event.preventDefault(), onCopy: () => footerUrl, tooltip: { nowrap: true, renderTooltip: () => 'Copy link' } }))),
                !!caretCodeText && showCaretCode && (React.createElement(React.Fragment, null,
                    React.createElement("span", { style: { opacity: 0.25 } }, '→'),
                    React.createElement(CopyCode_1.CopyCode, { value: caretCodeText, truncate: true, style: { maxWidth: 220 }, alt: true, spacious: true, roundest: true }))),
                !!caretMathTex && showCaretCode && (React.createElement(React.Fragment, null,
                    React.createElement("span", { style: { opacity: 0.25 } }, '→'),
                    React.createElement(CopyCode_1.CopyCode, { value: caretMathTex, truncate: true, style: { maxWidth: 220 }, alt: true, spacious: true, roundest: true })))))),
        React.createElement("div", { className: footerGroupClass },
            React.createElement(TranslitFooterPill_1.TranslitFooterPill, null),
            !!selectionSummary && showSelectionSummary && (React.createElement(React.Fragment, null,
                React.createElement("span", null, selectionSummary),
                React.createElement("span", { style: { opacity: 0.25 } }, '•'))),
            React.createElement("span", null, (0, util_1.pluralize)(wordCount, 'word')),
            showCharacterCount && (React.createElement(React.Fragment, null,
                React.createElement("span", { style: { opacity: 0.25 } }, '•'),
                React.createElement("span", null, (0, util_1.pluralize)(characterCount, 'character')))))));
};
exports.MuTxtFooter = MuTxtFooter;
